import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription, takeUntil, timer } from 'rxjs';
import type {
    IPacsInstanceManifest,
    IPacsJob,
    IPacsPrepareResult,
    IPacsSeriesSummary,
    IPacsStudySummary,
    IPacsViewerSelectedState,
} from 'src/app/interfaces/pacs-viewer.interfaces';
import type {
    TPacsJobStatus,
    TPacsViewerAction,
    TPacsViewerPhase,
    TPacsViewerResolution,
} from 'src/app/interfaces/pacs-viewer.types';
import { PatientService } from 'src/app/services/patient.service';
import { PacsViewerService } from 'src/app/services/pacs-viewer.service';
import type { App as DwvApp } from 'src/lib/dwv';

@Component({
    selector: 'app-dicom-view-modal',
    templateUrl: './dicom-view-modal.component.html',
    styleUrls: ['./dicom-view-modal.component.scss'],
    providers: [PacsViewerService],
    standalone: false
})
export class DicomViewModalComponent implements AfterViewInit, OnDestroy {
    @Input() public historyType: string | null = null;
    @Input() public historyId: number | null = null;
    @Input() public action: TPacsViewerAction = 'VIEW';

    @ViewChild('frameScrollInput')
    public frameScrollInput?: ElementRef<HTMLInputElement>;
    @ViewChild('loaderSeriesProgress')
    public loaderSeriesProgress?: ElementRef<HTMLSpanElement>;

    public readonly layerDivID = 'layerGroup0';
    public readonly patient = {
        name: '',
        birthdate: '',
    };

    public phase: TPacsViewerPhase = 'RESOLVING';
    public errorMessage = '';
    public preparationProgress: number | null = null;
    public studies: IPacsStudySummary[] = [];
    public series: IPacsSeriesSummary[] = [];
    public selectedStudy: IPacsStudySummary | null = null;
    public nextPageRef: string | null = null;
    public lookupSessionRef: string | null = null;
    public selectedSessionRef: string | null = null;
    public currentIndex = 0;
    public currentFrame = 0;
    public frameCount = 0;
    public seriesPaths: string[] = [];
    public inputFrameWidth = 0;
    public seriesbarMenuOpened = false;
    public selectingStudyRef: string | null = null;
    public loadingMoreStudies = false;

    private dwvApp?: DwvApp;
    private dwvModule?: typeof import('src/lib/dwv');
    private viewerSessionStart?: Promise<void>;
    private initTimeout?: ReturnType<typeof setTimeout>;
    private seriesRequest?: Subscription;
    private archiveRequest?: Subscription;
    private viewLayerElement: HTMLDivElement | null = null;
    private isDestroyed = false;
    private readonly destroy$ = new Subject<void>();

    private readonly onDwvLoad = () => {
        if (!this.dwvApp) return;

        this.dwvApp.setTool('Scroll');
        this.phase = 'VIEWING';
        this.preparationProgress = null;
        this.currentFrame = 0;

        const frameInput = this.frameScrollInput?.nativeElement;
        if (frameInput) frameInput.max = Math.max(this.frameCount - 1, 0).toString();

        const framebar = document.querySelector(
            '.dicom__framebar',
        ) as HTMLDivElement | null;
        if (!framebar) return;

        if (window.innerWidth > 768) {
            this.inputFrameWidth = framebar.getBoundingClientRect().height;
        } else {
            const framebarStyle = window.getComputedStyle(framebar);
            this.inputFrameWidth =
                framebar.clientWidth -
                parseFloat(framebarStyle.paddingLeft) * 2;
        }
    };

    private readonly onDwvLoadProgress = (event: { loaded?: number }) => {
        if (this.loaderSeriesProgress?.nativeElement) {
            this.loaderSeriesProgress.nativeElement.innerText = String(
                event.loaded ?? 0,
            );
        }
    };

    private readonly onDwvRenderEnd = () => {
        if (!this.dwvApp) return;

        const activeViewLayer = this.dwvApp
            .getLayerGroupByDivId(this.layerDivID)
            ?.getActiveViewLayer();
        if (!activeViewLayer) return;

        const values = activeViewLayer
            .getViewController()
            .getCurrentIndex()
            .getValues();
        const frame = values[2] ?? 0;
        this.currentFrame = frame;
        if (this.frameScrollInput?.nativeElement) {
            this.frameScrollInput.nativeElement.value = frame.toString();
        }
    };

    private readonly onDwvLoadError = () => {
        this.showError('Не удалось загрузить снимки выбранной серии');
    };

    private readonly onTouchStart = (event: TouchEvent) => {
        this.touchStartX = event.changedTouches[0]?.screenX ?? 0;
    };

    private readonly onTouchMove = (event: TouchEvent) => {
        if (this.phase !== 'VIEWING') return;

        const screenX = event.changedTouches[0]?.screenX;
        if (screenX === undefined) return;

        const movement = this.touchStartX - screenX;
        if (movement === 0 || movement % 15 !== 0) return;

        this.moveFrame(movement > 0 ? -1 : 1);
    };

    private touchStartX = 0;

    public constructor(
        patientService: PatientService,
        public readonly activeModal: NgbActiveModal,
        public readonly pacsViewer: PacsViewerService,
    ) {
        this.patient.name = patientService.getFullName;
        this.patient.birthdate = patientService.getBirthdate;
    }

    public ngAfterViewInit(): void {
        this.pacsViewer.setHistoryContext(this.historyType, this.historyId);

        // DWV has no initialisation event and needs its host element to settle.
        this.initTimeout = setTimeout(() => {
            this.initTouchListeners();
            this.startViewerSession();
        }, 300);
    }

    public ngOnDestroy(): void {
        this.isDestroyed = true;
        if (this.initTimeout) clearTimeout(this.initTimeout);
        this.destroy$.next();
        this.destroy$.complete();
        this.cancelSeriesLoad();
        this.archiveRequest?.unsubscribe();
        this.removeTouchListeners();
        this.disposeDwvApp();
        this.pacsViewer.resetSession();
    }

    public selectStudy(study: IPacsStudySummary): void {
        if (!this.lookupSessionRef || this.selectingStudyRef) return;

        this.selectingStudyRef = study.studyRef;
        this.pacsViewer
            .selectStudy(this.lookupSessionRef, study.studyRef)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (selected) => {
                    this.selectingStudyRef = null;
                    this.useSelectedStudy(selected);
                },
                error: () => {
                    this.selectingStudyRef = null;
                    this.showError('Не удалось выбрать исследование');
                },
            });
    }

    public loadMoreStudies(): void {
        if (
            !this.lookupSessionRef ||
            !this.nextPageRef ||
            this.loadingMoreStudies
        )
            return;

        this.loadingMoreStudies = true;
        this.pacsViewer
            .getStudies(this.lookupSessionRef, this.nextPageRef)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (page) => {
                    this.studies = [...this.studies, ...page.studies].filter(
                        (study, index, allStudies) =>
                            allStudies.findIndex(
                                (candidate) =>
                                    candidate.studyRef === study.studyRef,
                            ) === index,
                    );
                    this.nextPageRef = page.nextPageRef;
                    this.loadingMoreStudies = false;
                },
                error: () => {
                    this.loadingMoreStudies = false;
                    this.showError('Не удалось получить список исследований');
                },
            });
    }

    public switchSerie(index: number, event?: MouseEvent): void {
        event?.stopPropagation();
        const selectedSeries = this.series[index];
        if (!selectedSeries?.viewerSupported || !this.selectedSessionRef) return;

        this.cancelSeriesLoad();
        this.currentFrame = 0;
        this.currentIndex = index;
        this.frameCount = 0;
        this.seriesPaths = [];
        this.seriesbarMenuOpened = false;
        this.resetApp();
        this.prepareSeries(selectedSeries);
    }

    public onSeriesScrollInputChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.switchSerie(Number(input.value));
    }

    public onFrameInputChange(event: Event): void {
        if (!this.dwvApp) return;

        const input = event.target as HTMLInputElement;
        this.currentFrame = Number(input.value);
        this.render(this.currentFrame);
    }

    public onViewWheel(event: WheelEvent): void {
        if (this.phase !== 'VIEWING') return;
        this.moveFrame(event.deltaY > 0 ? 1 : -1);
    }

    public onMenuMobileButtonClick(event: MouseEvent): void {
        event.stopPropagation();
        this.seriesbarMenuOpened = !this.seriesbarMenuOpened;
    }

    public closeApp(event: MouseEvent): void {
        event.stopPropagation();
        this.activeModal.close();
    }

    public retry(): void {
        this.errorMessage = '';
        this.phase = 'RESOLVING';
        this.startViewerSession();
    }

    public formatStudyDate(value: string | null): string {
        if (!value) return 'Дата не указана';
        const compactDate = value.match(/^(\d{4})(\d{2})(\d{2})$/);
        if (compactDate) return `${compactDate[3]}.${compactDate[2]}.${compactDate[1]}`;
        return value;
    }

    public trackStudy(_: number, study: IPacsStudySummary): string {
        return study.studyRef;
    }

    public trackSeries(_: number, item: IPacsSeriesSummary): string {
        return item.seriesRef;
    }

    private startViewerSession(): void {
        if (this.viewerSessionStart || this.isDestroyed) return;

        this.viewerSessionStart = this.initializeViewerSession().finally(() => {
            this.viewerSessionStart = undefined;
        });
    }

    private async initializeViewerSession(): Promise<void> {
        try {
            if (this.action === 'VIEW') await this.ensureDwvApp();
            if (this.isDestroyed) return;
            this.resolveViewerSession();
        } catch {
            if (!this.isDestroyed) {
                this.showError('Не удалось загрузить модуль просмотра снимков');
            }
        }
    }

    private async ensureDwvApp(): Promise<void> {
        if (this.dwvApp) return;

        const dwvModule = this.dwvModule ?? (await import('src/lib/dwv'));
        if (this.isDestroyed) return;

        this.dwvModule = dwvModule;
        this.initApp();
    }

    private resolveViewerSession(): void {
        const historyType = this.historyType?.trim();
        const historyId = this.historyId;
        if (!historyType || historyId === null || !Number.isFinite(historyId)) {
            this.showError('Не удалось определить запись истории');
            return;
        }

        this.pacsViewer
            .resolveSession(historyType, historyId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (resolution) => this.handleResolution(resolution),
                error: () =>
                    this.showError('Не удалось найти DICOM-исследование'),
            });
    }

    private handleResolution(resolution: TPacsViewerResolution): void {
        switch (resolution.status) {
            case 'NOT_FOUND':
                this.phase = 'NOT_FOUND';
                return;
            case 'AMBIGUOUS':
                this.lookupSessionRef = resolution.lookupSessionRef;
                this.studies = resolution.studies;
                this.nextPageRef = resolution.nextPageRef;
                this.phase = resolution.studies.length
                    ? 'SELECT_STUDY'
                    : 'NOT_FOUND';
                return;
            case 'RESOLVED':
                this.useSelectedStudy({
                    ...resolution,
                    status: 'SELECTED',
                });
        }
    }

    private useSelectedStudy(selected: IPacsViewerSelectedState): void {
        this.selectedSessionRef = selected.selectedSessionRef;
        this.selectedStudy = selected.study;
        if (this.action === 'DOWNLOAD') {
            this.prepareArchive();
        } else {
            this.loadSeries();
        }
    }

    private loadSeries(): void {
        if (!this.selectedSessionRef) return;

        this.phase = 'LOADING_SERIES';
        this.pacsViewer
            .getSeries(this.selectedSessionRef)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (manifest) => {
                    this.series = manifest.series;
                    const firstSupportedIndex = this.series.findIndex(
                        (item) => item.viewerSupported,
                    );
                    if (firstSupportedIndex === -1) {
                        this.showError(
                            'В исследовании нет серий, доступных для просмотра',
                        );
                        return;
                    }
                    this.switchSerie(firstSupportedIndex);
                },
                error: () =>
                    this.showError('Не удалось получить серии исследования'),
            });
    }

    private prepareSeries(series: IPacsSeriesSummary): void {
        if (!this.selectedSessionRef) return;
        this.phase = 'PREPARING_SERIES';
        this.preparationProgress = null;

        this.seriesRequest = this.pacsViewer
            .prepareSeries(this.selectedSessionRef, series.seriesRef)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => this.handleSeriesPreparation(series, result),
                error: () =>
                    this.showError('Не удалось подготовить выбранную серию'),
            });
    }

    private handleSeriesPreparation(
        series: IPacsSeriesSummary,
        result: IPacsPrepareResult | IPacsJob,
        activeJobRef?: string,
    ): void {
        if (result.status === 'READY') {
            this.loadInstances(series);
            return;
        }
        if (this.isFailedStatus(result.status)) {
            this.showError('Не удалось подготовить выбранную серию');
            return;
        }

        this.preparationProgress =
            'progressPercent' in result ? result.progressPercent : null;
        const delay = this.getRetryDelay(result.retryAfterSeconds);
        if (result.status === 'METADATA_READY') {
            this.retrySeriesPreparation(series, delay);
            return;
        }
        const jobRef = 'jobRef' in result ? result.jobRef : activeJobRef;
        if (jobRef) {
            this.pollSeriesJob(series, jobRef, delay);
        } else {
            this.retrySeriesPreparation(series, delay);
        }
    }

    private pollSeriesJob(
        series: IPacsSeriesSummary,
        jobRef: string,
        delay: number,
    ): void {
        if (!this.selectedSessionRef) return;
        this.seriesRequest = timer(delay)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (!this.selectedSessionRef) return;
                this.seriesRequest = this.pacsViewer
                    .getJob(this.selectedSessionRef, jobRef)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (job) =>
                            this.handleSeriesPreparation(series, job, jobRef),
                        error: () =>
                            this.showError(
                                'Не удалось проверить подготовку серии',
                            ),
                    });
            });
    }

    private retrySeriesPreparation(
        series: IPacsSeriesSummary,
        delay: number,
    ): void {
        this.seriesRequest = timer(delay)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.prepareSeries(series));
    }

    private loadInstances(series: IPacsSeriesSummary): void {
        if (!this.selectedSessionRef) return;
        this.seriesRequest = this.pacsViewer
            .getInstances(this.selectedSessionRef, series.seriesRef)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (manifest) => this.loadDicomInstances(manifest),
                error: () =>
                    this.showError('Не удалось получить снимки серии'),
            });
    }

    private loadDicomInstances(manifest: IPacsInstanceManifest): void {
        if (!this.selectedSessionRef || !this.dwvApp) return;

        const instances = manifest.instances.filter(
            (instance) => instance.viewerSupported,
        );
        if (!instances.length) {
            this.showError('В серии нет снимков, доступных для просмотра');
            return;
        }

        this.frameCount = instances.reduce(
            (total, instance) => total + Math.max(instance.numberOfFrames, 1),
            0,
        );
        this.seriesPaths = instances.map((instance) =>
            this.pacsViewer.getDicomInstanceUrl(
                this.selectedSessionRef!,
                instance.instanceRef,
            ),
        );
        this.dwvApp.loadURLs(this.seriesPaths, {
            withCredentials: true,
            batchSize: 8,
        });
    }

    private prepareArchive(): void {
        if (!this.selectedSessionRef) return;
        this.archiveRequest?.unsubscribe();
        this.phase = 'PREPARING_ARCHIVE';
        this.preparationProgress = null;

        this.archiveRequest = this.pacsViewer
            .prepareArchive(this.selectedSessionRef)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => this.handleArchivePreparation(result),
                error: () => this.showError('Не удалось подготовить архив'),
            });
    }

    private handleArchivePreparation(
        result: IPacsPrepareResult | IPacsJob,
        activeJobRef?: string,
    ): void {
        if (result.status === 'READY') {
            if (!result.archiveRef || !this.selectedSessionRef) {
                this.showError('Ссылка на подготовленный архив недоступна');
                return;
            }
            this.downloadArchive(result.archiveRef);
            return;
        }
        if (this.isFailedStatus(result.status)) {
            this.showError('Не удалось подготовить архив');
            return;
        }

        this.preparationProgress =
            'progressPercent' in result ? result.progressPercent : null;
        const delay = this.getRetryDelay(result.retryAfterSeconds);
        if (result.status === 'METADATA_READY') {
            this.retryArchivePreparation(delay);
            return;
        }
        const jobRef = 'jobRef' in result ? result.jobRef : activeJobRef;
        if (jobRef) {
            this.pollArchiveJob(jobRef, delay);
        } else {
            this.retryArchivePreparation(delay);
        }
    }

    private pollArchiveJob(jobRef: string, delay: number): void {
        if (!this.selectedSessionRef) return;
        this.archiveRequest = timer(delay)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (!this.selectedSessionRef) return;
                this.archiveRequest = this.pacsViewer
                    .getJob(this.selectedSessionRef, jobRef)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (job) =>
                            this.handleArchivePreparation(job, jobRef),
                        error: () =>
                            this.showError(
                                'Не удалось проверить подготовку архива',
                            ),
                    });
            });
    }

    private retryArchivePreparation(delay: number): void {
        this.archiveRequest = timer(delay)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.prepareArchive());
    }

    private downloadArchive(archiveRef: string): void {
        if (!this.selectedSessionRef) return;
        const link = document.createElement('a');
        link.href = this.pacsViewer.getArchiveUrl(
            this.selectedSessionRef,
            archiveRef,
        );
        link.download = 'dicom-study.zip';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.phase = 'DOWNLOAD_READY';
    }

    private initApp(): void {
        if (!this.dwvModule || this.isDestroyed) return;

        const app = new this.dwvModule.App();
        app.init({
            dataViewConfigs: { '*': [{ divId: this.layerDivID }] },
            tools: { Scroll: {} },
        });
        app.addEventListener('load', this.onDwvLoad);
        app.addEventListener('loadprogress', this.onDwvLoadProgress);
        app.addEventListener('renderend', this.onDwvRenderEnd);
        app.addEventListener('loaderror', this.onDwvLoadError);
        this.dwvApp = app;
    }

    private resetApp(): void {
        this.disposeDwvApp();
        document
            .getElementById(this.layerDivID)
            ?.querySelectorAll('*')
            .forEach((element) => element.remove());
        this.initApp();
    }

    private disposeDwvApp(): void {
        if (!this.dwvApp) return;
        this.dwvApp.abortLoad();
        this.dwvApp.removeEventListener('load', this.onDwvLoad);
        this.dwvApp.removeEventListener('loadprogress', this.onDwvLoadProgress);
        this.dwvApp.removeEventListener('renderend', this.onDwvRenderEnd);
        this.dwvApp.removeEventListener('loaderror', this.onDwvLoadError);
        this.dwvApp.reset();
        this.dwvApp = undefined;
    }

    private initTouchListeners(): void {
        this.viewLayerElement = document.querySelector('.dicom__viewlayer');
        this.viewLayerElement?.addEventListener('touchstart', this.onTouchStart);
        this.viewLayerElement?.addEventListener('touchmove', this.onTouchMove);
    }

    private removeTouchListeners(): void {
        this.viewLayerElement?.removeEventListener(
            'touchstart',
            this.onTouchStart,
        );
        this.viewLayerElement?.removeEventListener('touchmove', this.onTouchMove);
        this.viewLayerElement = null;
    }

    private cancelSeriesLoad(): void {
        this.seriesRequest?.unsubscribe();
        this.seriesRequest = undefined;
        this.dwvApp?.abortLoad();
    }

    private moveFrame(delta: number): void {
        const nextFrame = this.currentFrame + delta;
        if (nextFrame < 0) {
            this.switchToAdjacentSeries(-1);
            return;
        }
        if (nextFrame >= this.frameCount) {
            this.switchToAdjacentSeries(1);
            return;
        }
        this.currentFrame = nextFrame;
        this.render(nextFrame);
    }

    private switchToAdjacentSeries(direction: -1 | 1): void {
        if (!this.series.length) return;
        let index = this.currentIndex;
        for (let checked = 0; checked < this.series.length; checked++) {
            index = (index + direction + this.series.length) % this.series.length;
            if (this.series[index].viewerSupported) {
                this.switchSerie(index);
                return;
            }
        }
    }

    private render(index: number): void {
        if (!this.dwvApp || !this.dwvModule) return;
        const activeViewLayer = this.dwvApp
            .getLayerGroupByDivId(this.layerDivID)
            ?.getActiveViewLayer();
        if (!activeViewLayer) return;

        const controller = activeViewLayer.getViewController();
        const values = controller.getCurrentIndex().getValues();
        values[2] = index;
        controller.setCurrentIndex(new this.dwvModule.Index(values));
    }

    private showError(message: string): void {
        this.errorMessage = message;
        this.phase = 'ERROR';
        this.preparationProgress = null;
    }

    private isFailedStatus(status: TPacsJobStatus): boolean {
        return status === 'FAILED' || status === 'EXPIRED';
    }

    private getRetryDelay(retryAfterSeconds: number | null): number {
        return Math.max(retryAfterSeconds ?? 1, 1) * 1000;
    }
}
